import React, { useState } from "react";
import { useCashierStore } from "../../store/cashier.store";
import { call, callableMessage, newIdempotencyKey } from "../../services/callables";
import { PaymentMode } from "../../types/index";
import { Button } from "../../components/common/Button";
import { Card } from "../../components/common/Card";
import { toast } from "react-hot-toast";
import { Banknote, CreditCard, Wallet, CheckCircle, Printer, ArrowLeft } from "lucide-react";

interface Props {
  draftId: string;
  cashierUid: string;
  cashierPhone?: string;
  onSuccess: () => void;
  onBack: () => void;
}

const CashierPaymentSection: React.FC<Props> = ({
  draftId,
  cashierUid,
  cashierPhone,
  onSuccess,
  onBack,
}) => {
  const { pipelineOrders, removeDraft } = useCashierStore();
  const draft = pipelineOrders.find((d) => d.id === draftId);

  const [paymentMode, setPaymentMode] = useState<PaymentMode>("cash");
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderCompleteId, setOrderCompleteId] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => newIdempotencyKey(`csh_${draftId}`.slice(0, 40)));

  if (!draft) return <div className="text-white text-center p-10">Error: Draft not found</div>;

  // --- ORDER CREATION LOGIC ---
  // Created server-side (cashierPlaceOrder): one order per kitchen (+ parent when the draft spans
  // kitchens), cashier "due" ledger entry and dueBalance. Idempotent per draft attempt.
  const handlePlaceOrder = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await call("cashierPlaceOrder", {
        items: draft.items,
        paymentMode,
        customerName: draft.customerName || "Walk-in",
        customerPhone: draft.customerPhone || "",
        orderType: draft.orderType,
        cashierPhone: cashierPhone || "",
        idempotencyKey,
      });

      setOrderCompleteId("success"); 
      removeDraft(draftId); 
      toast.success("Order Placed Successfully!");
    } catch (error) {
      console.error(error);
      toast.error(callableMessage(error, "Failed to place order"));
    } finally {
      setIsProcessing(false);
    }
  };

  // --- SUCCESS VIEW ---
  if (orderCompleteId) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-green-50 p-6 text-center animate-in zoom-in">
        <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mb-6 shadow-sm">
          <CheckCircle size={48} className="text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">Order Confirmed!</h2>
        <p className="text-gray-600 mb-8 text-lg">
          Kitchen has received the order. <br/> 
          Amount: <span className="font-bold font-mono">₹{draft.totalAmount}</span>
        </p>
        
        <div className="flex gap-4 w-full max-w-md">
            <Button variant="secondary" className="flex-1 py-4" onClick={() => window.print()}>
                <Printer className="mr-2"/> Print Bill
            </Button>
            <Button className="flex-1 py-4 bg-green-600 hover:bg-green-700" onClick={onSuccess}>
                Next Order
            </Button>
        </div>
      </div>
    );
  }

  // --- PAYMENT SELECTION VIEW ---
  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-8 flex items-center justify-center">
      <Card className="w-full max-w-2xl bg-white text-slate-900 shadow-2xl overflow-hidden">
        
        {/* Header */}
        <div className="bg-slate-50 p-6 border-b border-slate-100 flex justify-between items-center">
            <div className="flex items-center gap-4">
                <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft/></button>
                <h2 className="text-2xl font-bold text-slate-800">Confirm Payment</h2>
            </div>
            <div className="text-right">
                <p className="text-xs text-slate-500 uppercase font-bold">Total Amount</p>
                <p className="text-3xl font-mono font-bold text-blue-600">₹{draft.totalAmount}</p>
            </div>
        </div>

        <div className="p-8 space-y-8">
            
            {/* Mode Selection */}
            <div className="grid grid-cols-3 gap-4">
                <button
                    onClick={() => setPaymentMode("cash")}
                    className={`p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${
                        paymentMode === "cash" 
                        ? "border-green-500 bg-green-50 text-green-700 shadow-md transform scale-105" 
                        : "border-slate-100 hover:border-slate-300 text-slate-500"
                    }`}
                >
                    <Banknote size={32} />
                    <span className="font-bold">Cash</span>
                </button>

                <button
                    onClick={() => setPaymentMode("upi")}
                    className={`p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${
                        paymentMode === "upi" 
                        ? "border-blue-500 bg-blue-50 text-blue-700 shadow-md transform scale-105" 
                        : "border-slate-100 hover:border-slate-300 text-slate-500"
                    }`}
                >
                    <CreditCard size={32} />
                    <span className="font-bold">UPI / QR</span>
                </button>

                <button
                    onClick={() => setPaymentMode("food")}
                    className={`p-6 rounded-xl border-2 flex flex-col items-center justify-center gap-3 transition-all ${
                        paymentMode === "food" 
                        ? "border-orange-500 bg-orange-50 text-orange-700 shadow-md transform scale-105" 
                        : "border-slate-100 hover:border-slate-300 text-slate-500"
                    }`}
                >
                    <Wallet size={32} />
                    <span className="font-bold text-center leading-tight">Food / Pay Later</span>
                </button>
            </div>

            {/* Warning / Info */}
            <div className={`p-4 rounded-lg flex gap-3 text-sm ${paymentMode === 'food' ? 'bg-orange-50 text-orange-800' : 'bg-blue-50 text-blue-800'}`}>
                <CheckCircle className="shrink-0" size={20}/>
                <p>
                    {paymentMode === 'food' 
                        ? "Payment will be collected by Delivery Agent. Order status will be 'Unpaid'."
                        : `Ensure payment of ₹${draft.totalAmount} is received before confirming. It will be logged in your settlement.`
                    }
                </p>
            </div>

            {/* Actions */}
            <div className="pt-4">
                <Button 
                    onClick={handlePlaceOrder}
                    isLoading={isProcessing}
                    className="w-full py-4 text-lg bg-slate-900 hover:bg-black shadow-xl"
                >
                    Confirm & Print
                </Button>
            </div>
        </div>
      </Card>
    </div>
  );
};

export default CashierPaymentSection;